/**
 * Figma export showcase.
 *
 * Renders one component at a time on an isolated stage so a designer can
 * import each into Figma (via a DOM-import plugin such as html.to.design)
 * without surrounding canvas chrome. Use the backdrop toggle to pick the
 * surface that best shows the component's glass — "Transparent" gives the
 * cleanest import.
 */

import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useSearchParams } from "react-router-dom";
import { cn } from "@/lib/cn";
import { LayoutProvider } from "@/components/layout/layout-provider";
import { CHROME_GROUPS } from "./export-chrome";
import { FRAME_GROUP } from "./export-frames";
import { APP_GROUP } from "./export-app";
import type { ExportEntry, ExportGroup } from "./export-types";

const GROUPS: ExportGroup[] = [APP_GROUP, ...CHROME_GROUPS, FRAME_GROUP];

const ALL_ENTRIES: ExportEntry[] = GROUPS.flatMap((g) => g.entries);

type BackdropId = "canvas" | "dark" | "light" | "transparent";

const BACKDROPS: Array<{ id: BackdropId; label: string }> = [
  { id: "canvas", label: "Canvas" },
  { id: "dark", label: "Dark" },
  { id: "light", label: "Light" },
  { id: "transparent", label: "Transparent" },
];

const BACKDROP_CLASS: Record<BackdropId, string> = {
  canvas: "bg-canvas",
  dark: "bg-[#0A0A0B]",
  light: "bg-[#F7F7F4]",
  transparent: "",
};

// Checkerboard to signal a transparent surface (cannot be expressed via tokens).
const CHECKERBOARD_STYLE: CSSProperties = {
  backgroundColor: "#1b1b1f",
  backgroundImage:
    "linear-gradient(45deg, #2a2a30 25%, transparent 25%), linear-gradient(-45deg, #2a2a30 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #2a2a30 75%), linear-gradient(-45deg, transparent 75%, #2a2a30 75%)",
  backgroundSize: "20px 20px",
  backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0",
};

export function ExportPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [backdrop, setBackdrop] = useState<BackdropId>("canvas");

  const selectedId = searchParams.get("id") ?? ALL_ENTRIES[0]?.id ?? "";
  function setSelectedId(id: string) {
    setSearchParams({ id }, { replace: true });
  }

  // Match the shapes showcase: enable native-glass styling so frames render
  // with their production glass tokens.
  useEffect(() => {
    const root = document.documentElement;
    const previous = {
      denk: root.dataset.desktopDenk,
      glass: root.dataset.desktopGlass,
      nativeGlass: root.dataset.desktopNativeGlass,
    };
    root.dataset.desktopDenk = "true";
    root.dataset.desktopGlass = "true";
    root.dataset.desktopNativeGlass = "true";
    return () => {
      if (previous.denk === undefined) delete root.dataset.desktopDenk;
      else root.dataset.desktopDenk = previous.denk;
      if (previous.glass === undefined) delete root.dataset.desktopGlass;
      else root.dataset.desktopGlass = previous.glass;
      if (previous.nativeGlass === undefined) delete root.dataset.desktopNativeGlass;
      else root.dataset.desktopNativeGlass = previous.nativeGlass;
    };
  }, []);

  const selected = useMemo(
    () => ALL_ENTRIES.find((e) => e.id === selectedId) ?? ALL_ENTRIES[0],
    [selectedId],
  );

  return (
    <div className="flex h-full bg-[#0A0A0B] text-white" data-testid="export-page">
      {/* Component nav */}
      <aside className="flex w-64 shrink-0 flex-col overflow-y-auto border-r border-white/[0.06] p-3">
        <div className="px-2 py-2">
          <h1 className="text-sm font-bold text-white">Figma Export</h1>
          <p className="mt-1 text-[11px] leading-4 text-white/40">
            Pick a component, then import this page's URL into a Figma DOM plugin
            (e.g. html.to.design).
          </p>
        </div>
        {GROUPS.map((group) => (
          <div key={group.category} className="mb-3">
            <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white/30">
              {group.category}
            </div>
            <div className="flex flex-col gap-0.5">
              {group.entries.map((entry) => (
                <div key={entry.id} className="group/nav flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setSelectedId(entry.id)}
                    data-testid={`export-nav-${entry.id}`}
                    className={cn(
                      "flex-1 rounded-md px-2 py-1.5 text-left text-xs transition-colors",
                      entry.id === selected?.id
                        ? "bg-accent/15 text-white"
                        : "text-white/55 hover:bg-white/[0.05] hover:text-white",
                    )}
                  >
                    {entry.label}
                  </button>
                  <a
                    href={`/workshop/export/${entry.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open isolated URL (point Figma plugin here)"
                    className="shrink-0 rounded p-0.5 text-white/20 opacity-0 transition-opacity hover:text-white/60 group-hover/nav:opacity-100"
                    data-testid={`export-isolated-link-${entry.id}`}
                  >
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 9L9 1M9 1H4M9 1V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </div>
              ))}
            </div>
          </div>
        ))}
      </aside>

      {/* Stage */}
      <main className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-4 border-b border-white/[0.06] px-5 py-3">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold text-white">
              {selected?.label}
            </h2>
            {selected?.description ? (
              <p className="mt-0.5 truncate text-[11px] text-white/40">
                {selected.description}
              </p>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {BACKDROPS.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => setBackdrop(b.id)}
                data-testid={`export-backdrop-${b.id}`}
                className={cn(
                  "rounded-md border px-2.5 py-1 text-[11px] transition-colors",
                  backdrop === b.id
                    ? "border-accent/40 bg-accent/15 text-white"
                    : "border-white/10 text-white/50 hover:bg-white/[0.05]",
                )}
              >
                {b.label}
              </button>
            ))}
          </div>
        </header>

        <div
          className={cn(
            "flex flex-1 items-center justify-center overflow-auto p-12",
            BACKDROP_CLASS[backdrop],
          )}
          style={backdrop === "transparent" ? CHECKERBOARD_STYLE : undefined}
          data-testid="export-stage"
        >
          <LayoutProvider>{selected?.render()}</LayoutProvider>
        </div>
      </main>
    </div>
  );
}
